import React, {useEffect, useMemo, useState} from 'react'
import Text from '@component/Text/Text'
import {IUserRank} from '@api/rank/rank'
import MoreButton from '@feature/common/components/MoreButton/MoreButton'
import IconDown from '@svg/common/ic_btn_down.svg'
import {useQueryGetAllLeagues} from '@hook/useLeagues'
import {useTranslation} from '@lib/intl/useTranslations'
import {useQueryLeagueDetail} from '@feature/leaderboard/hooks/useQueryLeagueDetail'
import {useRouter} from 'next/router'
import LeagueUserCell from '@feature/leaderboard/components/LeagueUserCell/LeagueUserCell'
import useLogin from '@hook/useLogin'
import useQueryRankLeague from '@hook/query/leaderboard/useQueryRankLeague'
import LeagueSummaryBoard from '@feature/leaderboard/components/LeagueSummaryBoard/LeagueSummaryBoard'
import LeagueSideList from '@feature/leaderboard/components/LeagueSideList/LeagueSideList'
import {isEmptyArray, isNotEmptyArray} from '@util/array'
import EmptyCell from '@feature/leaderboard/components/EmptyCell/EmptyCell'
import useQueryFetchMe from '@hook/query/account/useQueryFetchMe'
import useQueryMyRank from '@hook/query/leaderboard/useQueryMyRank'
import IconCheck from '@svg/common/ic_check.svg'
import {useQueryGetShowLeagueSidebar} from '@hook/query/rank/league/useQueryGetShowLeagueSidebar'
import {useLoginStore} from '@store/LoginStore'
import {createLog} from '@util/logs'
import {useRankLeaderBoardFilter} from '@hook/useLeaderBoardFilter'
import useWindowSize from '@hook/useWindowSize'
import {isNotEmpty} from '@util/strings'
import LeagueLeaderboardHeaderContainer from '@feature/leaderboard/pages/ScreenLeague/LeagueHeaders/LeagueLeaderboardHeaderContainer'
import SkeletonBlock from '@component/skeleton/SkeletonBlock'
import Spinner from '@component/Spinner/Spinner'
import useScreenLog from '@hook/useScreenLog'
import SearchBar from '@feature/leaderboard/components/SearchBar/SearchBar'
import FilterButton from '@feature/leaderboard/components/FilterButton/FilterButton'
import {useLoginModalStore} from '@store/LoginModalStore'

const LeaderboardScreenLeague = () => {
    const t = useTranslation()
    const {} = useScreenLog({pageName: 'leaderboard_league'})

    const {replace, query, isReady, locale} = useRouter()
    const {isLogin} = useLogin()

    const {isMd} = useWindowSize()

    const {RankFilter, setRankFilter} = useRankLeaderBoardFilter()
    const {rankKind} = RankFilter

    const [currentLeagueID, setCurrentLeagueID] = useState<string>(query.league as string)

    const {data: topLeagues} = useQueryGetAllLeagues()
    const {data: showLeagues} = useQueryGetShowLeagueSidebar()
    const {data: me} = useQueryFetchMe(undefined, false)
    const {
        data: leagueData,
        isLoading: isBoardLoading,
        isFetching: isBoardFetch,
        isSuccess: isBoardSuccess,
    } = useQueryLeagueDetail(currentLeagueID, me?.id)
    const {data: meSimpleData} = useQueryMyRank()

    const myId = useLoginStore(state => state.id)
    const showLoginModal = useLoginModalStore(state => state.showLoginModal)

    const {
        data: rankData,
        isFetchingNextPage,
        isLoading,
        isReachingEnd,
        fetchNextPage,
        refetch,
        isFetching,
        isSuccess,
    } = useQueryRankLeague(currentLeagueID, !myId ? 'normal' : rankKind)

    const [expandedRank, setExpandedRank] = useState(undefined)

    useEffect(() => {
        setExpandedRank(undefined)
    }, [leagueData, rankKind])

    const onToggleExpandItem = (idx?: number) => {
        if (isNotEmpty(expandedRank + 1) && expandedRank === idx) {
            setExpandedRank(undefined)
        } else {
            setExpandedRank(idx)
        }
    }

    useEffect(() => {
        if (!topLeagues || isEmptyArray(topLeagues) || !isReady) return

        if (query.league) {
            setCurrentLeagueID(query.league as string)
        } else {
            const now = new Date().getTime() / 1000
            const activeLeagues = topLeagues?.filter(league => league?.is_active === true)
            const progressLeagues = activeLeagues?.filter(league => league?.start_at < now && now < league?.end_at)
            const waitingLeagues = activeLeagues?.filter(league => now < league?.start_at)

            if (showLeagues) {
                setCurrentLeagueID(showLeagues?.leagues?.at(0)?.id)
            } else if (isNotEmptyArray(progressLeagues)) {
                setCurrentLeagueID(progressLeagues?.at(0)?.id)
            } else if (isNotEmptyArray(waitingLeagues)) {
                setCurrentLeagueID(waitingLeagues?.at(0)?.id)
            } else {
                setCurrentLeagueID(activeLeagues?.at(0)?.id)
            }
        }
    }, [query, isReady, topLeagues, showLeagues])

    const onToggleFollowing = () => {
        if (!isLogin()) {
            showLoginModal()
            return
        }
        if (rankKind === 'following') {
            setRankFilter({rankKind: 'normal'})
        } else {
            setRankFilter({rankKind: 'following'})
        }
        createLog('event', 'leaderboard_leagues_sort_following', {rankKind: rankKind})
    }

    const userRankList = useMemo(() => {
        let list: IUserRank[] = []
        if (leagueData?.my_league_rank !== 0 && me && me?.id) {
            list.push({
                id: me?.id,
                name: locale === 'ko' && me?.nicknames.at(0)?.nickname ? me?.nicknames.at(0)?.nickname : me?.name,
                thumbnail: me?.thumbnail,
                active_exchanges: me?.active_exchanges,
                rank: leagueData?.my_league_rank,
                score: leagueData?.my_league_score,
                is_following: true,
                is_pro: me?.is_pro,
                simple_graph: meSimpleData?.simple_graph,
            })
        }

        rankData?.pages?.map(page => list.push(...(page ?? [])))

        return list
    }, [leagueData, me, rankData, locale, meSimpleData?.simple_graph])

    const onLeagueChange = (leagueID: string) => {
        createLog('event', 'leagues_select_league_info', {league_id: leagueID})
        setCurrentLeagueID(leagueID)
        void replace(
            {
                pathname: '/leaderboard',
                query: {tab: 'league', league: leagueID},
            },
            undefined,
            {shallow: true},
        )
    }

    const isCheckMoreButton = useMemo(() => {
        if (!myId) {
            return true
        } else if (rankKind === 'following' || isReachingEnd) {
            return false
        } else {
            return true
        }
    }, [myId, rankKind, isReachingEnd])

    const onPressMoreButton = async () => {
        createLog('event', 'leaderboard_league_more', {league_id: currentLeagueID})
        await fetchNextPage()
    }

    return (
        <div className={'grid grid-cols-[350px_auto] gap-x-[20px] lg:block'}>
            <div>
                <LeagueSideList onChangedLeague={onLeagueChange} leagueId={currentLeagueID} state={'ongoing'} />
            </div>
            <div>
                <div className={'min-h-[200px] md:min-h-[100%]'}>
                    {isBoardLoading && isBoardFetch && !isBoardSuccess ? (
                        <SkeletonBlock
                            className={'min-h-[200px] md:min-h-[164px]'}
                            sizeClassName={'h-full !rounded-[5px]'}
                        />
                    ) : (
                        currentLeagueID && (
                            <LeagueSummaryBoard
                                leagueId={currentLeagueID}
                                league={leagueData}
                                leagueIsLoading={isBoardLoading}
                                leagueRefetch={() => refetch()}
                            />
                        )
                    )}
                </div>

                <div className={'flex justify-between items-center mt-[10px]'}>
                    <FilterButton
                        showLeftTick={true}
                        dataCy={'btn_leaderboard_main_following'}
                        title={t('leaderboard.filter.button.following')}
                        isSelected={rankKind === 'following'}
                        onClick={onToggleFollowing}
                    />
                    <SearchBar />
                </div>
                <LeagueLeaderboardHeaderContainer />
                <div data-cy={'leaderboard_table_league'}>
                    {!isLoading ? (
                        <>
                            {userRankList.length > 0 ? (
                                userRankList?.map((user, index) => {
                                    return (
                                        <LeagueUserCell
                                            leagueId={currentLeagueID}
                                            kind="profit"
                                            key={`${user?.rank}_${index}`}
                                            dataCy={'item_leaderboard_league_user'}
                                            {...user}
                                            onPress={() => onToggleExpandItem(index)}
                                            isExpanded={!isMd && index === expandedRank}
                                        />
                                    )
                                })
                            ) : (
                                <EmptyCell />
                            )}
                        </>
                    ) : (
                        <Spinner visible={true} className={'mt-[30px] mx-auto'} />
                    )}
                </div>

                {isCheckMoreButton && (
                    <div
                        className={`flex justify-center min-h-[60px] ${
                            isLoading || isFetchingNextPage ? 'h-screen' : 'mt-[20px]'
                        }`}>
                        {!isLoading && (
                            <MoreButton
                                dataCy={'btn_leaderboard_league_more'}
                                onClick={onPressMoreButton}
                                isLoading={isLoading || isFetchingNextPage}
                                renderRight={
                                    <>
                                        <IconDown width={16} className={'fill-gray01 dark:fill-dark_gray01'} />
                                    </>
                                }
                            />
                        )}
                    </div>
                )}
            </div>
        </div>
    )
}

export default LeaderboardScreenLeague
